import { type, Schema, MapSchema } from "@colyseus/schema";
import { BasketballDimensions } from "@shared/constants/BasketballDimensions";

export enum PlayerState {
    IDLE = 'idle',
    MOVING = 'moving',
    SHOOTING = 'shooting',
    PASSING = 'passing',
    BLOCKING = 'blocking',
    CELEBRATING = 'celebrating',
    DEFEATED = 'defeated'
}

export enum PlayerAnimation {
  IDLE = 'idle',
  WALKING = 'walking',
  RUNNING = 'running',
  IDLE_WITH_BALL = 'idle_with_ball',
  WALKING_WITH_BALL = 'walking_with_ball',
  RUNNING_WITH_BALL = 'running_with_ball',
  SHOOTING = 'shooting',
  PASSING = 'passing',
  BLOCKING = 'blocking',
};

export enum BallAnimation {
    IDLE = 'idle',
    DRIBBLE = 'dribble'
};    

export enum Team {
  A = 'A',
  B = 'B'
}

export class SVector3 extends Schema {
  @type("number") x: number = 0;
  @type("number") y: number = 0;
  @type("number") z: number = 0;
}

export class SQuaternion extends Schema {
  @type("number") x: number = 0;
  @type("number") y: number = 0;
  @type("number") z: number = 0;
  @type("number") w: number = 1; // Default to identity quaternion
}

export class DynamicBody extends Schema {
  @type(SVector3) position = new SVector3();
  @type(SQuaternion) rotation = new SQuaternion();
  @type(SVector3) velocity = new SVector3();
  @type(SVector3) targetPosition = new SVector3(); // Keep targetPosition if needed for player movement logic
  @type(SQuaternion) targetRotation = new SQuaternion(); // Keep targetRotation if needed for player movement logic
  @type("string") animation: string = ""; // Animation might belong here or in Player, let's keep it here for now
}

export class PlayerSchema extends Schema {
  @type("string") id: string;
  @type("number") avatarIndex: number = 0;
  @type("string") username: string = "";
  @type("string") team: Team = Team.A;
  @type(DynamicBody) dynamicBody = new DynamicBody();
  @type("number") score: number = 0;
  @type("boolean") hasBall: boolean = false;
  @type("string") playerState: PlayerState = PlayerState.IDLE;
  @type("boolean") connected: boolean = true;
}

export class RigidBody extends Schema {
  @type("string") id: string;
  @type("string") type: string = ""; // box, sphere, etc.
  @type("number") color: number = 0xFF0000;
  @type("number") x: number = 0;
  @type("number") y: number = 0;
  @type("number") z: number = 0;
  @type("number") rotationX: number = 0;
  @type("number") rotationY: number = 0;
  @type("number") rotationZ: number = 0;
  @type("number") width: number = 0;
  @type("number") height: number = 0;
  @type("number") depth: number = 0;
  @type("number") radius: number = 0; // For spheres
  @type("number") radiusTop: number = 0; // For cylinders
  @type("number") radiusBottom: number = 0; // For cylinders
  @type("boolean") wireframe: boolean = false; // For visualization
}

export class BallSchema extends Schema {
  @type(DynamicBody) dynamicBody = new DynamicBody();
  @type("number") radius: number = BasketballDimensions.ball.radius;
  @type("number") bounceFactor: number = BasketballDimensions.ball.bounceFactor;
  @type("string") possessedBy: string = "";
  @type("boolean") isMoving: boolean = false; // Does this need dynamicBody.velocity magnitude check? Keep for now.

  @type("number") shooterX: number = 0; // Position where the shot was taken from
  @type("number") shooterZ: number = 0; // Position where the shot was taken from
}

export class BasketballRoomState extends Schema {
  @type({ map: PlayerSchema }) players = new MapSchema<PlayerSchema>();
  @type(BallSchema) ball = new BallSchema();
  @type({ map: RigidBody }) rigidBodies = new MapSchema<RigidBody>();
  @type("number") homeScore: number = 0;
  @type("number") awayScore: number = 0;
  @type("number") gameTime: number = 3600; // 1 hour in seconds
  @type("boolean") gameStarted: boolean = false;
  @type("boolean") gamePaused: boolean = false;
} 